/**
 * 評価チャート共通の小 UI: radar/推移 の表示切替と、任意系列の凡例。
 *
 * デザイン評価 (MyPage スキル評価タブ) と DLE 評価 (MyPage DLE評価タブ) が同じ見た目の
 * 切替 pill と凡例を使う。ラベルは i18n 名前空間が feature ごとに違うので、文言は
 * 呼び出し側から props で受け、ここは見た目だけを持つ。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 評価チャートの radar/推移 切替 pill と系列凡例の共通コンポーネント
 * @graph-connects none
 */

/** 評価チャートの表示モード。 @graph-connects none */
export type EvalMode = 'radar' | 'line';

/**
 * radar / 推移 を切替える pill segmented control。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 評価チャートの radar / 推移 表示切替 UI
 * @graph-connects none
 */
export function EvalModeSwitch({
  mode,
  onChange,
  radarLabel,
  lineLabel,
  ariaLabel,
}: {
  mode: EvalMode;
  onChange: (next: EvalMode) => void;
  radarLabel: string;
  lineLabel: string;
  ariaLabel: string;
}) {
  const segClass = (active: boolean) =>
    `px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition-colors leading-none ${
      active
        ? 'bg-[var(--color-bg-surface)] text-[var(--color-text-primary)] shadow-sm'
        : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
    }`;
  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-full p-0.5 border border-[var(--color-border)]"
      style={{ background: 'var(--color-bg-elevated)' }}
      role="group"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        onClick={() => {
          onChange('radar');
        }}
        className={segClass(mode === 'radar')}
      >
        {radarLabel}
      </button>
      <button
        type="button"
        onClick={() => {
          onChange('line');
        }}
        className={segClass(mode === 'line')}
      >
        {lineLabel}
      </button>
    </div>
  );
}

/**
 * 任意系列の radar 凡例 (色 + ラベルの並び)。
 *
 * @graph-node {Page}
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business radar の系列凡例 (色見本 + ラベル) の共通表示
 * @graph-connects none
 */
export function RadarLegend({ items }: { items: { color: string; label: string }[] }) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 text-xs text-[var(--color-text-muted)]">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-0.5 rounded"
            style={{ background: it.color }}
            aria-hidden="true"
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}
